import { ArrowLeft, Camera, FileText, ImagePlus, Package, PackageCheck, User } from 'lucide-react';
import type { NomenclatureDto } from '@eombor/contracts';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { HandoverLineDraft, HandoverPhotoDraft } from '../model/handover.store.js';
import { PhotoGrid } from './PhotoGrid.js';
import { PhotoPicker } from './PhotoPicker.js';

/**
 * BERISH — bitta ekran, bitta harakat.
 *
 * Ilgari bu yerda to'rtta alohida bosqich bor edi: miqdor → rasm → oluvchi
 * → tasdiq. Ulardan uchtasi skladchidan HECH NARSA so'ramasdi:
 *
 *   - miqdor tasdiqlanganiga aynan teng bo'lishi shart, ya'ni o'zgartirib
 *     bo'lmasdi — ekran shunchaki ko'rsatardi;
 *   - oluvchining ismi talabnomada allaqachon bor edi («Kim orqali»), uni
 *     esa skladchi noldan qaytadan yozardi;
 *   - tasdiq ekrani qolgan ikkitasining takrori edi.
 *
 * Qolgan yagona haqiqiy amal — SURAT olish. Shuning uchun ekran shu
 * atrofida qurilgan: yuqorida "nima va kimga" (o'qish uchun), o'rtada
 * kamera (yagona tugma), pastda "Berildi".
 *
 * Skladchi qo'lqopda, telefonda va ombor yorug'ligida ishlaydi — har
 * qo'shimcha ekran uning qo'lidan bir tegish o'g'irlaydi.
 */

const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

export interface IssueStepProps {
  docNo: string | null;
  /** Qog'ozdagi «ТРЕБОВАНИЕ №» — mahsulot shu raqam bo'yicha beriladi. */
  formNo: string | null;
  /** «Kim orqali» — talabnomadan keladi, bu yerda TAHRIRLANMAYDI. */
  throughPerson: string | null;
  lines: readonly HandoverLineDraft[];
  nomenclatures: ReadonlyMap<string, NomenclatureDto>;
  photos: readonly HandoverPhotoDraft[];
  comment: string;
  onComment: (comment: string) => void;
  onPickPhotos: (files: File[]) => void;
  onRemovePhoto: (id: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  pending: boolean;
  /** Tayyor matn (i18n dan) yoki `null` — tugma yonida turadi. */
  blocker: string | null;
  error: string | null;
}

export const IssueStep = ({
  docNo,
  formNo,
  throughPerson,
  lines,
  nomenclatures,
  photos,
  comment,
  onComment,
  onPickPhotos,
  onRemovePhoto,
  onSubmit,
  onBack,
  pending,
  blocker,
  error,
}: IssueStepProps) => (
  <div className="flex flex-col gap-4">
    {/*
      SARLAVHA — qog'ozdagi raqam eng katta element.
      Olib ketayotgan odamning qo'lidagi qog'ozda aynan shu raqam yozilgan
      va skladchining birinchi ishi ikkovini solishtirish. Talabnoma
      raqami ichki hujjat raqami — u ostida, xira.
    */}
    <header
      className={cn(
        'relative overflow-hidden rounded-[var(--radius-lg)] border border-border',
        'bg-surface p-4 shadow-[var(--shadow-1)]',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -top-20 -right-12 size-52 rounded-full',
          'bg-accent-soft opacity-70 blur-2xl',
        )}
      />

      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3.5">
          <span
            aria-hidden
            className={cn(
              'flex size-12 shrink-0 items-center justify-center rounded-[var(--radius-md)]',
              'bg-accent text-[var(--color-accent-contrast)] shadow-[var(--shadow-1)]',
            )}
          >
            <FileText size={22} />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-xs text-text-faint">{t('handover.confirmForm')}</span>
            <span className="tabular truncate text-xl leading-7 font-semibold text-text">
              {formNo ?? '—'}
            </span>
            <span className="tabular truncate text-xs text-text-faint">{docNo ?? '—'}</span>
          </span>
        </div>

        {/*
          KIM OLADI — o'qish uchun, kiritish uchun EMAS.
          Buyurtmachi buni talabnomada ko'rsatgan; skladchi uni faqat
          qog'ozdagi ism bilan solishtiradi.
        */}
        <div className="flex min-w-0 items-center gap-2.5">
          <span aria-hidden className="text-text-faint">
            <User size={16} />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-xs text-text-faint">{t('handover.confirmReceiver')}</span>
            <span className="truncate text-sm font-medium text-text">
              {throughPerson ?? t('handover.receiverUnknown')}
            </span>
          </span>
        </div>
      </div>
    </header>

    {/*
      NIMA BERILADI — ixcham ro'yxat.
      Miqdor bu yerda tahrirlanmaydi va tahrirlanmasligi kerak ham: u
      zanjirda uch marta kelishilgan (korxona, sklad, buxgalteriya).
    */}
    <ul className="flex flex-col gap-1.5">
      {lines.map((line) => {
        const nomenclature = nomenclatures.get(line.nomenclatureId);
        const unit = nomenclature === undefined ? '' : unitText(nomenclature.unit);
        /* Korxona kesgan bo'lsa buni skladchi KO'RISHI kerak: u qog'ozdagi
           son bilan ekrandagini solishtiradi. */
        const cut = line.approvedQty < line.requestedQty;

        return (
          <li
            key={line.lineId}
            className={cn(
              'flex items-center gap-3 rounded-[var(--radius-md)] border border-border',
              'bg-surface px-3 py-2.5',
            )}
          >
            <span aria-hidden className="shrink-0 text-text-faint">
              <Package size={16} />
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-text">
              {nomenclature?.name ?? `#${line.nomenclatureId}`}
            </span>
            <span className="flex shrink-0 items-baseline gap-1.5">
              {/* Raqam katta va `tabular`: skladchi uni bir metr naridan,
                  qo'lqopda va ombor yorug'ligida o'qiydi. */}
              <span className="tabular text-lg leading-6 font-semibold text-text">
                {qtyText(line.issuedQty)}
              </span>
              <span className="text-xs text-text-muted">{unit}</span>
              {cut ? (
                <span className="tabular text-xs text-warn">
                  / {qtyText(line.requestedQty)}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>

    {/* FOTO-HISOBOT — ekrandagi YAGONA haqiqiy amal, shuning uchun
        kamera tugmasi to'ldirilgan va birinchi turadi. */}
    <div className="flex flex-wrap items-center gap-2">
      <PhotoPicker
        label={t('handover.photoCamera')}
        icon={<Camera size={16} aria-hidden />}
        capture="environment"
        tone="primary"
        onPick={onPickPhotos}
      />
      <PhotoPicker
        label={t('handover.photoPick')}
        icon={<ImagePlus size={16} aria-hidden />}
        onPick={onPickPhotos}
      />
      {photos.length === 0 ? (
        <span className="text-sm text-text-muted">{t('handover.photoHint')}</span>
      ) : (
        <span className="tabular text-sm text-text-muted">
          {t('handover.photoCount', { count: photos.length })}
        </span>
      )}
    </div>

    <PhotoGrid photos={photos} onRemove={onRemovePhoto} />

    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-text">{t('handover.comment')}</span>
      <textarea
        rows={2}
        value={comment}
        maxLength={500}
        placeholder={t('handover.commentPlaceholder')}
        onChange={(event) => onComment(event.target.value)}
        className={cn(
          'w-full resize-y rounded-[var(--radius-sm)] border border-border-control bg-surface',
          'px-3 py-2 text-base text-text placeholder:text-text-faint hover:border-border-strong',
        )}
      />
    </label>

    {error === null ? null : (
      <p role="alert" className="text-sm text-danger">
        {error}
      </p>
    )}

    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-3 rounded-[var(--radius-lg)]',
        'border border-border bg-surface-2 px-4 py-3',
      )}
    >
      {/* To'siq SABABI tugma yonida: o'chirilgan tugma yolg'iz turgan
          bo'lsa, skladchi "ishlamayapti" deb o'ylaydi. */}
      {blocker === null ? null : (
        <span className="mr-auto text-sm text-text-muted">{blocker}</span>
      )}
      <Button variant="ghost" onClick={onBack} disabled={pending}>
        <ArrowLeft size={16} aria-hidden />
        {t('handover.back')}
      </Button>
      <Button size="lg" loading={pending} disabled={blocker !== null} onClick={onSubmit}>
        <PackageCheck size={18} aria-hidden />
        {t('handover.submit')}
      </Button>
    </div>
  </div>
);
